import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const TEACHERS = [
  { initials: 'K',  name: 'Mrs. Kei',             subject: 'Guidance Counselor',    bg: '#2C6B4D' },
  { initials: 'JC', name: 'Mr. James Chen',        subject: 'Mathematics',           bg: '#1E4D6B' },
  { initials: 'SM', name: 'Dr. Sarah Martinez',    subject: 'Science',               bg: '#2C5F3A' },
  { initials: 'ET', name: 'Ms. Emily Thompson',    subject: 'English & Literature',  bg: '#5B3472' },
  { initials: 'DW', name: 'Mr. David Williams',    subject: 'History & Social Studies', bg: '#6B3A1E' },
  { initials: 'AJ', name: 'Ms. Aisha Johnson',     subject: 'Computer Science',      bg: '#1A4A6B' },
  { initials: 'RD', name: 'Ms. Rosa Delgado',      subject: 'Art & Creative Arts',   bg: '#6B2A2A' },
  { initials: 'TB', name: 'Mr. Thomas Brown',      subject: 'Music',                 bg: '#4A3A6B' },
  { initials: 'YT', name: 'Ms. Yuki Tanaka',       subject: 'Foreign Languages',     bg: '#2A5A5A' },
  { initials: 'MR', name: 'Coach Marcus Reynolds', subject: 'PE & Health',           bg: '#4A5A1E' },
  { initials: 'HM', name: 'Ms. Hana Mori',         subject: 'Manga & Sequential Art', bg: '#7A3060' },
  { initials: 'AR', name: 'Ms. Alex Rivera',       subject: 'Digital Art & Design',  bg: '#3D5A80' },
  { initials: 'JP', name: 'Mr. Jordan Park',       subject: 'Game Design',           bg: '#1A5C40' },
  { initials: 'CT', name: 'Mr. Chris Torres',      subject: 'Python & Programming',  bg: '#2B4C7E' },
  { initials: 'PN', name: 'Ms. Priya Nair',        subject: 'Financial Literacy',    bg: '#5C4A00' },
];

const BRIDGE_PROGRAMS_LANDING = [
  { id: 'math',    name: 'Math Jumpstart',           icon: '📐', weeks: 6, spw: 3, price: 59,  originalPrice: undefined, accent: '#0EA5E9' },
  { id: 'reading', name: 'Reading & Writing Level-Up', icon: '📖', weeks: 6, spw: 3, price: 59,  originalPrice: undefined, accent: '#8B5CF6' },
  { id: 'coding',  name: 'Coding Fundamentals',       icon: '💻', weeks: 8, spw: 3, price: 69,  originalPrice: undefined, accent: '#10B981' },
  { id: 'bundle',  name: 'Full Foundation Bundle',    icon: '🌟', weeks: 6, spw: 4, price: 89,  originalPrice: 118,       accent: '#F59E0B' },
];

const DOMAINS = [
  { label: 'Core Academics', color: '#1D4E89', bg: '#EEF3FA', desc: 'Math, Science, English, History, Computer Science, Foreign Languages, Music, Art, PE' },
  { label: 'Creative Arts', color: '#7A3060', bg: '#F7F0F5', desc: 'Manga & Sequential Art, Digital Art & Design — with specialist artists, not generalists' },
  { label: 'Coding & Technology', color: '#1A5C38', bg: '#EEF6F1', desc: 'Python & Programming, Game Design — project-based, build something real' },
  { label: 'Life Skills', color: '#7A5000', bg: '#F7F3EC', desc: 'Financial Literacy: budgeting, investing, stock market, Roth IRA, taxes, credit' },
];

const FEATURES = [
  {
    label: 'Socratic method',
    body: 'Every teacher guides students to discover answers themselves. No spoon-feeding — only the questions that build real understanding that survives a test.',
  },
  {
    label: 'Grade-calibrated',
    body: "Sessions adapt to your child's grade level and confidence. 5th-grade algebra is taught very differently from 10th-grade — same teacher, different approach.",
  },
  {
    label: 'Parents stay informed',
    body: 'Every session ends with a structured summary: what was covered, what clicked, and what needs more practice. No guessing how the session went.',
  },
  {
    label: 'Show your work',
    body: "Students can upload a photo of their worksheet, sketch, or code screenshot. Teachers review it directly — no more \"I'm stuck but I can't explain where.\"",
  },
];

type MsgFrom = 'kei' | 'student' | 'teacher' | 'divider';
interface DemoMsg { from: MsgFrom; text: string; }
interface Scenario {
  id: string; label: string; status: string;
  teacher: { name: string; initials: string; bg: string };
  messages: DemoMsg[];
  delays: number[];
}

const SCENARIOS: Scenario[] = [
  {
    id: 'math', label: 'Math', status: 'MATH · GRADE 9',
    teacher: { name: 'Mr. Chen', initials: 'JC', bg: '#1E4D6B' },
    messages: [
      { from: 'kei',     text: "Hey! I'm Mrs. Kei — I'll connect you with the right teacher. What are we tackling today?" },
      { from: 'student', text: "Quadratic equations — I have a test tomorrow and I keep making errors." },
      { from: 'kei',     text: "Perfect timing. Let me connect you with Mr. Chen right now." },
      { from: 'divider', text: 'Mr. Chen joined' },
      { from: 'teacher', text: "Before we touch the formula — what do you get when you plug x = 2 into x² + 3x − 10? Walk me through it." },
      { from: 'student', text: "Umm... 4 + 6 − 10, so that's 0?" },
      { from: 'teacher', text: "Exactly right. x = 2 is a root. So what does that tell us about how this factors?" },
    ],
    delays: [400, 1700, 3100, 3900, 4900, 6300, 7600],
  },
  {
    id: 'python', label: 'Python', status: 'PYTHON · GRADE 8',
    teacher: { name: 'Mr. Torres', initials: 'CT', bg: '#2B4C7E' },
    messages: [
      { from: 'kei',     text: "Hi! What are you coding today?" },
      { from: 'student', text: "My for loop prints all numbers but I only want even ones." },
      { from: 'kei',     text: "Mr. Torres is the one for this — connecting you now." },
      { from: 'divider', text: 'Mr. Torres joined' },
      { from: 'teacher', text: "Don't fix anything yet — share exactly what you wrote." },
      { from: 'student', text: "for i in range(10):  print(i)" },
      { from: 'teacher', text: "Good. Tell me: when i = 3, is 3 even or odd? And how would Python check that?" },
    ],
    delays: [400, 1700, 3000, 3800, 4700, 6000, 7300],
  },
  {
    id: 'manga', label: 'Manga', status: 'MANGA · GRADE 7',
    teacher: { name: 'Ms. Mori', initials: 'HM', bg: '#7A3060' },
    messages: [
      { from: 'kei',     text: "Hi! What are you creating today?" },
      { from: 'student', text: "I'm drawing a fight scene but the panels feel flat and boring." },
      { from: 'kei',     text: "Ms. Mori specializes in exactly this — let me bring her in." },
      { from: 'divider', text: 'Ms. Mori joined' },
      { from: 'teacher', text: "Flat fight scenes usually mean every panel is the same distance. Which panel feels the most boring to you?" },
      { from: 'student', text: "Panel 3. It's just two characters facing each other." },
      { from: 'teacher', text: "Classic 'neutral shot.' What emotion should panel 3 feel — fear? shock? power? Let's pick one and design around it." },
    ],
    delays: [400, 1800, 3200, 4000, 5100, 6500, 7900],
  },
  {
    id: 'finance', label: 'Finance', status: 'FINANCE · GRADE 11',
    teacher: { name: 'Ms. Nair', initials: 'PN', bg: '#5C4A00' },
    messages: [
      { from: 'kei',     text: "Hi! What money topic are we tackling?" },
      { from: 'student', text: "My dad says I should open a Roth IRA but I have no idea what that even is." },
      { from: 'kei',     text: "Ms. Nair is our financial literacy expert — she'll break this down perfectly." },
      { from: 'divider', text: 'Ms. Nair joined' },
      { from: 'teacher', text: "Great question. First — do you know the difference between paying taxes now vs. paying taxes later?" },
      { from: 'student', text: "Not really... tax is just tax, right?" },
      { from: 'teacher', text: "That's the key insight. A Roth IRA is entirely about *when* you pay. Let's start there." },
    ],
    delays: [400, 1800, 3200, 4100, 5200, 6600, 7900],
  },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div style={root}>
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(5px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes typingBounce {
          0%, 60%, 100% { transform: translateY(0); }
          30%            { transform: translateY(-4px); }
        }
        @media (max-width: 768px) {
          .hero-grid { grid-template-columns: 1fr !important; }
          .hero-preview { display: none !important; }
          .features-grid { grid-template-columns: 1fr !important; }
          .pricing-grid { grid-template-columns: 1fr !important; }
          .teachers-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .nav-pad { padding: 0 20px !important; }
          .section-pad { padding-left: 24px !important; padding-right: 24px !important; }
        }
        button:focus-visible { outline: 2px solid #C8860A; outline-offset: 2px; }
        a:focus-visible { outline: 2px solid #C8860A; outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) {
          * { animation: none !important; transition: none !important; }
        }
      `}</style>

      {/* ── Nav ── */}
      <nav style={nav} className="nav-pad">
        <span style={logoType}>Academic Allies</span>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={() => navigate('/tour')} style={navLoginBtn}>See how it works</button>
          <button onClick={() => navigate('/login')} style={navLoginBtn}>Log in</button>
          <button onClick={() => navigate('/signup')} style={navCta}>Get started free</button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section style={{ background: '#0F1923', padding: '72px 48px 88px' }} className="section-pad">
        <div style={{ maxWidth: '1160px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 460px', gap: '56px', alignItems: 'center' }} className="hero-grid">
          <div>
            <p style={eyebrow}>AI Learning Center · K–12</p>
            <h1 style={heroH1}>
              The right tutor.<br />
              <em style={{ fontStyle: 'italic', color: '#C8860A' }}>Right now.</em>
            </h1>
            <p style={heroSub}>
              Fifteen specialist AI teachers across academics, creative arts, coding, and financial literacy — each trained in their subject's pedagogy. Guided by Mrs. Kei, a counselor who knows your student. Every session summarized for parents.
            </p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' }}>
              <button onClick={() => navigate('/signup')} style={heroCtaPrimary}>Start for free</button>
              <button onClick={() => navigate('/login')} style={heroCtaSecondary}>Log in</button>
            </div>
            <p style={{ fontSize: '12px', color: '#3A5060', margin: 0 }}>5 free sessions · No credit card required</p>
          </div>

          {/* Interactive demo */}
          <div className="hero-preview">
            <LiveDemo onSignup={() => navigate('/signup')} />
          </div>
        </div>
      </section>

      {/* ── Domains ── */}
      <section style={{ background: '#FFFFFF', padding: '64px 48px' }} className="section-pad">
        <div style={{ maxWidth: '1160px', margin: '0 auto' }}>
          <h2 style={sectionH2}>One learning center. Four worlds of knowledge.</h2>
          <p style={{ ...sectionSub, marginBottom: '32px' }}>
            Beyond the standard curriculum — specialist teachers in creative arts, coding, and real-world life skills that most schools don't cover.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '14px' }}>
            {DOMAINS.map(d => (
              <div key={d.label} style={{ background: d.bg, border: `1.5px solid ${d.color}22`, borderLeft: `4px solid ${d.color}`, borderRadius: '10px', padding: '20px 18px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: d.color, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>{d.label}</div>
                <div style={{ fontSize: '13px', color: '#374151', lineHeight: 1.65 }}>{d.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Teacher roster ── */}
      <section style={{ background: '#F0F4F8', padding: '52px 48px' }} className="section-pad">
        <div style={{ maxWidth: '1160px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h2 style={{ ...sectionH2, marginBottom: '6px' }}>Fifteen specialist educators, one coordinated team</h2>
              <p style={{ ...sectionSub, margin: 0, maxWidth: '600px' }}>
                Mrs. Kei connects each student to the right specialist. Every teacher is trained in their subject's pedagogy, not just the content.
              </p>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '7px' }} className="teachers-grid">
            {TEACHERS.map((t) => <TeacherCard key={t.initials} {...t} />)}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section style={{ background: '#FFFFFF', padding: '72px 48px' }} className="section-pad">
        <div style={{ maxWidth: '1160px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '40px' }} className="features-grid">
            {FEATURES.map((f) => (
              <div key={f.label}>
                <div style={{ width: '28px', height: '3px', background: '#2C6B4D', marginBottom: '20px' }} />
                <h3 style={featureH3}>{f.label}</h3>
                <p style={featureBody}>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section style={{ background: '#F0F4F8', padding: '72px 48px' }} className="section-pad">
        <div style={{ maxWidth: '1160px', margin: '0 auto' }}>
          <h2 style={sectionH2}>Simple pricing</h2>
          <p style={{ ...sectionSub, marginBottom: '40px' }}>Start free. Upgrade when you're ready.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }} className="pricing-grid">
            <PricingCard
              name="Free"
              price="$0"
              period=""
              desc="Try every teacher before committing"
              features={['All 15 teachers', '5 sessions total', 'Session summaries for parents']}
              cta="Start free"
              highlight={false}
              onCta={() => navigate('/signup')}
            />
            <PricingCard
              name="Student"
              price="$29"
              period="/mo"
              desc="Unlimited tutoring for one student"
              features={['Unlimited sessions', 'All 15 teachers', 'Session summaries', 'Progress tracking']}
              cta="Get started"
              highlight={true}
              onCta={() => navigate('/signup')}
            />
            <PricingCard
              name="Family"
              price="$49"
              period="/mo"
              desc="Up to 4 students, one account"
              features={['Everything in Student', 'Up to 4 children', 'Parent dashboard', 'Per-child session history']}
              cta="Get started"
              highlight={false}
              onCta={() => navigate('/signup')}
            />
          </div>
          <p style={{ marginTop: '24px', fontSize: '13px', color: '#7A8898', textAlign: 'center' }}>
            School or district?{' '}
            <a href="mailto:hello@a3lc.com" style={{ color: '#2C6B4D', textDecoration: 'none', fontWeight: 600 }}>
              Contact us
            </a>{' '}
            · $199/mo per school
          </p>
        </div>
      </section>

      {/* ── Summer Bridge ── */}
      <section style={{ background: '#FFFBEB', padding: '72px 48px', borderTop: '3px solid #FCD34D' }} className="section-pad">
        <div style={{ maxWidth: '1160px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#92400E', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '4px', padding: '3px 8px' }}>Limited Enrollment</span>
          </div>
          <h2 style={{ ...sectionH2, marginBottom: '10px' }}>☀️ Summer Bridge Program</h2>
          <p style={{ ...sectionSub, marginBottom: '32px' }}>
            A focused 6–8 week program to keep your child sharp — or get them ahead — before the next school year. One-time payment, no subscription. Sessions personalized to your child's level after a quick diagnostic quiz.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '14px', marginBottom: '32px' }}>
            {BRIDGE_PROGRAMS_LANDING.map(p => (
              <div key={p.id} style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderTop: `3px solid ${p.accent}`, borderRadius: '10px', padding: '20px 18px' }}>
                <div style={{ fontSize: '22px', marginBottom: '8px' }}>{p.icon}</div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '4px' }}>{p.name}</div>
                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '14px' }}>{p.weeks} weeks · {p.spw} sessions/week</div>
                {p.originalPrice && (
                  <span style={{ fontSize: '12px', color: '#9CA3AF', textDecoration: 'line-through', marginRight: '6px' }}>${p.originalPrice}</span>
                )}
                <span style={{ fontSize: '24px', fontWeight: 800, color: p.accent }}>${p.price}</span>
                {p.originalPrice && <span style={{ fontSize: '12px', color: '#059669', fontWeight: 700, marginLeft: '8px' }}>saves ${p.originalPrice - p.price}</span>}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/signup')} style={{ background: '#D97706', border: 'none', color: '#fff', fontSize: '15px', fontWeight: 700, cursor: 'pointer', padding: '13px 28px', borderRadius: '7px' }}>
              Enroll your child →
            </button>
            <span style={{ fontSize: '13px', color: '#92400E' }}>Create a parent account — then enroll from your dashboard</span>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ background: '#0F1923', padding: '88px 48px', textAlign: 'center' }} className="section-pad">
        <h2 style={{ ...heroH1, fontSize: '40px', marginBottom: '16px' }}>Start learning tonight.</h2>
        <p style={{ ...heroSub, maxWidth: '400px', margin: '0 auto 32px', fontSize: '16px' }}>
          No credit card. No setup. Just your student and the right teacher.
        </p>
        <button onClick={() => navigate('/signup')} style={{ ...heroCtaPrimary, fontSize: '16px', padding: '15px 32px' }}>
          Get 5 free sessions
        </button>
      </section>

      {/* ── Footer ── */}
      <footer style={footer} className="nav-pad">
        <span style={{ fontFamily: "Georgia,'Times New Roman',serif", fontSize: '15px', color: '#3A5060', fontWeight: 700 }}>
          Academic Allies
        </span>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
          <a href="mailto:hello@a3lc.com" style={{ fontSize: '13px', color: '#3A5060', textDecoration: 'none' }}>Contact</a>
          <a href="/faq" style={{ fontSize: '13px', color: '#3A5060', textDecoration: 'none' }}>FAQ</a>
          <a href="/privacy" style={{ fontSize: '13px', color: '#3A5060', textDecoration: 'none' }}>Privacy Policy</a>
          <button onClick={() => navigate('/login')} style={{ background: 'none', border: 'none', fontSize: '13px', color: '#3A5060', cursor: 'pointer', padding: 0 }}>
            Log in
          </button>
        </div>
        <span style={{ fontSize: '12px', color: '#253545' }}>
          © 2026 Academic Allies · Commonwealth of Virginia
        </span>
      </footer>
    </div>
  );
}

// ── LiveDemo ──────────────────────────────────────────────────────────────────

function LiveDemo({ onSignup }: { onSignup: () => void }) {
  const [activeId, setActiveId] = useState('math');
  const [visibleCount, setVisibleCount] = useState(0);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const scenario = SCENARIOS.find(s => s.id === activeId)!;

  useEffect(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    setVisibleCount(0);

    scenario.delays.forEach((delay, i) => {
      const t = setTimeout(() => setVisibleCount(i + 1), delay);
      timeoutsRef.current.push(t);
    });

    return () => { timeoutsRef.current.forEach(clearTimeout); };
  }, [activeId]);

  const visibleMessages = scenario.messages.slice(0, visibleCount);
  const nextMsg = scenario.messages[visibleCount];
  const showTyping = nextMsg !== undefined
    && nextMsg.from !== 'student'
    && nextMsg.from !== 'divider';

  return (
    <div style={{ background: '#131E2B', border: '1px solid #1E2E40', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

      {/* Tab bar */}
      <div style={{ background: '#0B141E', borderBottom: '1px solid #1A2A3A', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '10px', color: '#2E4A5A', fontWeight: 700, letterSpacing: '0.08em', marginRight: '6px', textTransform: 'uppercase' }}>
          Try:
        </span>
        {SCENARIOS.map(s => (
          <button
            key={s.id}
            onClick={() => setActiveId(s.id)}
            style={{
              background: s.id === activeId ? '#2C6B4D' : 'transparent',
              border: `1px solid ${s.id === activeId ? '#2C6B4D' : '#1E3040'}`,
              color: s.id === activeId ? '#fff' : '#4A7090',
              fontSize: '11px', fontWeight: 600,
              cursor: 'pointer', padding: '4px 11px',
              borderRadius: '4px', transition: 'all 0.15s',
              lineHeight: 1.4,
            }}
          >
            {s.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#2C6B4D' }} />
          <span style={{ fontSize: '10px', color: '#3A5868', fontWeight: 600, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
            {scenario.status}
          </span>
        </div>
      </div>

      {/* Messages */}
      <div style={{ padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: '9px', minHeight: '295px' }}>
        {visibleMessages.map((msg, i) => {
          if (msg.from === 'divider') {
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '3px 0', animation: 'fadeSlideIn 0.2s ease' }}>
                <div style={{ height: '1px', flex: 1, background: '#1A2D3D' }} />
                <span style={{ fontSize: '10px', color: '#2E4A5A', whiteSpace: 'nowrap', fontWeight: 600 }}>
                  {scenario.teacher.name} joined
                </span>
                <div style={{ height: '1px', flex: 1, background: '#1A2D3D' }} />
              </div>
            );
          }
          const isStudent = msg.from === 'student';
          const bubble: React.CSSProperties = isStudent
            ? { background: '#1A3050', color: '#A8C0D8', borderRadius: '10px 10px 2px 10px' }
            : msg.from === 'kei'
            ? { background: '#1A3326', color: '#8EC9A8', borderRadius: '10px 10px 10px 2px' }
            : { background: '#14231A', color: '#7FBA8F', borderRadius: '10px 10px 10px 2px' };
          return (
            <div key={i} style={{ display: 'flex', justifyContent: isStudent ? 'flex-end' : 'flex-start', animation: 'fadeSlideIn 0.22s ease' }}>
              <div style={{ ...bubble, fontSize: '12.5px', lineHeight: 1.55, padding: '8px 12px', maxWidth: '87%' }}>
                {msg.text}
              </div>
            </div>
          );
        })}

        {showTyping && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', animation: 'fadeSlideIn 0.2s ease' }}>
            <div style={{ background: '#1A3326', borderRadius: '10px 10px 10px 2px', padding: '10px 14px', display: 'flex', gap: '4px', alignItems: 'center' }}>
              {[0, 0.18, 0.36].map((delay, di) => (
                <div key={di} style={{
                  width: '5px', height: '5px', borderRadius: '50%', background: '#4A9070',
                  animation: `typingBounce 0.9s ${delay}s infinite`,
                }} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid #1A2A3A', padding: '11px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#0B141E' }}>
        <span style={{ fontSize: '11px', color: '#2E4A5A', letterSpacing: '0.01em' }}>5 free sessions · No card needed</span>
        <button
          onClick={onSignup}
          style={{ background: '#2C6B4D', border: 'none', color: '#fff', fontSize: '12px', fontWeight: 600, cursor: 'pointer', padding: '7px 16px', borderRadius: '5px' }}
        >
          Try it yourself →
        </button>
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function TeacherCard({ initials, name, subject, bg }: { initials: string; name: string; subject: string; bg: string }) {
  return (
    <div style={{ background: '#FFFFFF', border: '1px solid #E0E8F0', borderRadius: '8px', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '9px' }}>
      <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#fff', fontWeight: 700, flexShrink: 0 }}>
        {initials}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '12px', fontWeight: 700, color: '#1A2332', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
        <div style={{ fontSize: '10px', color: '#7A8898', marginTop: '1px' }}>{subject}</div>
      </div>
    </div>
  );
}

function PricingCard({ name, price, period, desc, features, cta, highlight, onCta }: {
  name: string; price: string; period: string; desc: string;
  features: string[]; cta: string; highlight: boolean; onCta: () => void;
}) {
  return (
    <div style={{
      background: highlight ? '#0F1923' : '#FFFFFF',
      border: `1px solid ${highlight ? '#2C6B4D' : '#E0E8F0'}`,
      borderRadius: '10px', padding: '28px 24px',
      display: 'flex', flexDirection: 'column', gap: '20px',
    }}>
      <div>
        <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: highlight ? '#C8860A' : '#7A8898', marginBottom: '8px' }}>
          {name}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '2px', marginBottom: '6px' }}>
          <span style={{ fontFamily: "Georgia,'Times New Roman',serif", fontSize: '40px', fontWeight: 400, color: highlight ? '#FFFFFF' : '#1A2332', lineHeight: 1 }}>
            {price}
          </span>
          {period && <span style={{ fontSize: '15px', color: highlight ? '#7A90A4' : '#7A8898' }}>{period}</span>}
        </div>
        <div style={{ fontSize: '13px', color: highlight ? '#7A90A4' : '#5D7185' }}>{desc}</div>
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '9px', flex: 1 }}>
        {features.map((f) => (
          <li key={f} style={{ display: 'flex', gap: '9px', fontSize: '13px', color: highlight ? '#8A9AAA' : '#5D7185' }}>
            <span style={{ color: '#2C6B4D', fontWeight: 700, flexShrink: 0 }}>✓</span>
            {f}
          </li>
        ))}
      </ul>
      <button onClick={onCta} style={{
        background: highlight ? '#2C6B4D' : '#EEF2F7',
        border: 'none', color: highlight ? '#fff' : '#2C6B4D',
        fontSize: '14px', fontWeight: 600, cursor: 'pointer',
        padding: '12px', borderRadius: '6px', width: '100%',
      }}>
        {cta}
      </button>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const root: React.CSSProperties = {
  fontFamily: "system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
  color: '#1A2332',
  overflowX: 'hidden',
};

const nav: React.CSSProperties = {
  position: 'sticky', top: 0, zIndex: 100,
  background: '#0F1923', borderBottom: '1px solid #1A2A3A',
  padding: '0 48px', height: '60px',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
};

const logoType: React.CSSProperties = {
  fontFamily: "Georgia,'Times New Roman',serif",
  fontSize: '18px', fontWeight: 700, color: '#FFFFFF', letterSpacing: '-0.01em',
};

const navLoginBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: '#7A8898',
  fontSize: '14px', fontWeight: 500, cursor: 'pointer', padding: '8px 14px', borderRadius: '5px',
};

const navCta: React.CSSProperties = {
  background: '#2C6B4D', border: 'none', color: '#fff',
  fontSize: '14px', fontWeight: 600, cursor: 'pointer', padding: '9px 18px', borderRadius: '6px',
};

const eyebrow: React.CSSProperties = {
  margin: '0 0 16px', fontSize: '11px', fontWeight: 700,
  letterSpacing: '0.12em', textTransform: 'uppercase', color: '#C8860A',
};

const heroH1: React.CSSProperties = {
  margin: '0 0 20px',
  fontFamily: "Georgia,'Times New Roman',serif",
  fontSize: '52px', fontWeight: 400, lineHeight: 1.12, color: '#FFFFFF',
  textWrap: 'balance' as React.CSSProperties['textWrap'],
};

const heroSub: React.CSSProperties = {
  margin: '0 0 32px', fontSize: '17px', lineHeight: 1.65, color: '#7A90A4', maxWidth: '420px',
};

const heroCtaPrimary: React.CSSProperties = {
  background: '#2C6B4D', border: 'none', color: '#fff',
  fontSize: '15px', fontWeight: 600, cursor: 'pointer', padding: '13px 26px', borderRadius: '6px',
};

const heroCtaSecondary: React.CSSProperties = {
  background: 'none', border: '1px solid #243346', color: '#7A90A4',
  fontSize: '15px', fontWeight: 500, cursor: 'pointer', padding: '13px 26px', borderRadius: '6px',
};

const sectionH2: React.CSSProperties = {
  margin: '0 0 10px',
  fontFamily: "Georgia,'Times New Roman',serif",
  fontSize: '28px', fontWeight: 400, color: '#1A2332',
};

const sectionSub: React.CSSProperties = {
  margin: '0 0 32px', fontSize: '15px', lineHeight: 1.6, color: '#5D7185', maxWidth: '560px',
};

const featureH3: React.CSSProperties = {
  margin: '0 0 10px',
  fontFamily: "Georgia,'Times New Roman',serif",
  fontSize: '20px', fontWeight: 400, color: '#1A2332',
};

const featureBody: React.CSSProperties = {
  margin: 0, fontSize: '15px', lineHeight: 1.65, color: '#5D7185',
};

const footer: React.CSSProperties = {
  background: '#080F17', padding: '28px 48px',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px',
};
